/**
 * Simple logger utility for PolarCraft
 * Can be disabled in production for cleaner console
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Default: enable in development, disable in production
const config: LoggerConfig = {
  enabled: import.meta.env.DEV,
  minLevel: 'warn',
};

function shouldLog(level: LogLevel): boolean {
  return config.enabled && LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

export const logger = {
  debug: (message: string, ...args: unknown[]) => {
    if (shouldLog('debug')) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  },

  info: (message: string, ...args: unknown[]) => {
    if (shouldLog('info')) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },

  warn: (message: string, ...args: unknown[]) => {
    if (shouldLog('warn')) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },

  error: (message: string, ...args: unknown[]) => {
    if (shouldLog('error')) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  },

  /** Configure logger settings */
  configure: (newConfig: Partial<LoggerConfig>) => {
    Object.assign(config, newConfig);
  },

  /** Enable/disable logging */
  setEnabled: (enabled: boolean) => {
    config.enabled = enabled;
  },
};

export default logger;
